/**
 * Voice input shared by the generation modals.
 *
 * Extracted from PostgenController so the video modal can offer "speak what you need"
 * without a second copy of the MediaRecorder plumbing. Each caller gets its own recorder
 * instance: two modals must never share recording state.
 */
angular.module("galilai.creator").factory("transcribeService", function (api, $timeout) {
    // Hard cap so a forgotten open mic does not upload a huge file
    const MAX_DURATION_SECONDS = 150;

    function createRecorder(options) {
        options = options || {};
        // The default cap guards against a forgotten open mic; the voice sample uses a much
        // smaller one, because 30 seconds is enough and long audio only makes the clone
        // worse
        const maxSeconds = options.maxSeconds || MAX_DURATION_SECONDS;

        let mediaRecorder = null;
        let audioChunks = [];
        let recordingTimeout = null;
        let recording = false;
        let transcribing = false;

        function notify() {
            if (options.onState) {
                options.onState({
                    recording: recording,
                    transcribing: transcribing,
                });
            }
            $timeout(angular.noop);
        }

        function fail(key) {
            if (options.onError) {
                options.onError(key);
                return;
            }
            showError(gettext(key));
        }

        function upload(blob, mimeType) {
            transcribing = true;
            notify();

            const formData = new FormData();
            formData.append("audio", blob, "audio.webm");

            return fetch(api.getBaseURL() + "/transcribe", {
                headers: {
                    Authorization: "Bearer " + api.getTokenSync(),
                },
                method: "POST",
                body: formData,
            })
                .then(function (response) {
                    return response.json();
                })
                .then(function (data) {
                    if (!data?.text) {
                        fail("transcribe.error");
                        return;
                    }
                    if (options.onText) {
                        options.onText(data.text);
                    }
                })
                .catch(function (err) {
                    console.error(err);
                    fail("transcribe.error_start");
                })
                .finally(function () {
                    transcribing = false;
                    notify();
                });
        }

        function stop() {
            clearTimeout(recordingTimeout);

            if (mediaRecorder && mediaRecorder.state !== "inactive") {
                mediaRecorder.stop();
            }

            recording = false;
            notify();
        }

        function start() {
            navigator.mediaDevices
                .getUserMedia({
                    audio: true,
                })
                .then(function (stream) {
                    let mimeType = "audio/webm";
                    if (!MediaRecorder.isTypeSupported(mimeType)) {
                        mimeType = "audio/mp4";
                    }

                    mediaRecorder = new MediaRecorder(stream, {
                        mimeType: mimeType + ";codecs=opus",
                    });

                    audioChunks = [];

                    mediaRecorder.ondataavailable = function (e) {
                        if (e.data.size > 0) {
                            audioChunks.push(e.data);
                        }
                    };

                    mediaRecorder.onerror = function () {
                        fail("transcribe.error_start");
                    };

                    mediaRecorder.onstop = function () {
                        try {
                            const blob = new Blob(audioChunks, { type: mimeType });
                            // Whoever wants the audio itself (the lookalike's voice sample)
                            // gets the blob and skips transcription: same recording,
                            // different output
                            if (options.onBlob) {
                                options.onBlob(blob, mimeType);
                                notify();
                                return;
                            }
                            upload(blob, mimeType);
                        } catch (err) {
                            transcribing = false;
                            notify();
                        }
                    };

                    mediaRecorder.start();
                    recording = true;

                    recordingTimeout = setTimeout(function () {
                        if (mediaRecorder.state !== "inactive") {
                            mediaRecorder.stop();
                            recording = false;
                            $timeout(angular.noop);
                        }
                    }, maxSeconds * 1000);

                    notify();
                })
                .catch(function (err) {
                    console.error(err);
                    if (err.name === "NotAllowedError") {
                        fail("transcribe.no_permission");
                    } else {
                        fail("transcribe.error_start");
                    }
                });
        }

        return {
            toggle: function () {
                if (recording) {
                    stop();
                    return;
                }
                start();
            },
            stop: stop,
            isRecording: function () {
                return recording;
            },
            isTranscribing: function () {
                return transcribing;
            },
        };
    }

    return {
        createRecorder: createRecorder,
    };
});
